// Composants de pilotage : indicateurs, écarts, statuts, sources, jauges, filtres.

import type { ReactNode } from 'react';
import { TONES, type Tone } from './Badge.js';
import { cn } from './cn.js';

/** Surtitre + titre de section, avec source et complément. */
export function SectionHeader({
  title,
  sub,
  source,
  actions,
  id,
}: {
  title: ReactNode;
  sub?: ReactNode;
  source?: ReactNode;
  actions?: ReactNode;
  id?: string;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
      <div className="min-w-0">
        <h2
          id={id}
          className="flex flex-wrap items-center gap-2 font-display text-[13px] font-normal uppercase tracking-[0.18em] text-ink-2"
        >
          {title}
          {source}
        </h2>
        {sub && <p className="mt-1 max-w-[80ch] text-[13.5px] text-ink-3">{sub}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export type DeltaTone = 'good' | 'bad' | 'flat';
const DELTA: Record<DeltaTone, string> = {
  good: 'bg-success-50 text-success-700',
  bad: 'bg-danger-50 text-danger-700',
  flat: 'bg-surface-2 text-ink-3',
};

/** Écart à une référence : couleur selon le sens favorable, jamais d'émoji. */
export function DeltaBadge({
  tone,
  children,
  label,
}: {
  tone: DeltaTone;
  children: ReactNode;
  label?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px]">
      {label && <span className="text-ink-3">{label}</span>}
      <span className={cn('tnum rounded-pill px-2 py-0.5 font-semibold', DELTA[tone])}>{children}</span>
    </span>
  );
}

/** Tuile d'indicateur. */
export function KpiTile({
  label,
  value,
  sub,
  delta,
  alert,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  delta?: ReactNode;
  alert?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1 bg-surface px-5 py-4', className)}>
      <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</span>
      <span
        className={cn(
          'truncate font-display text-[26px] font-bold leading-tight',
          alert ? 'text-danger-700' : 'text-ink',
        )}
      >
        {value}
      </span>
      {sub && <span className="text-[13px] text-ink-3">{sub}</span>}
      {delta && <span className="mt-1.5">{delta}</span>}
    </div>
  );
}

/** Rangée de tuiles séparées par des filets, dans un cadre unique. */
export function KpiStrip({ children, cols = 4 }: { children: ReactNode; cols?: 4 | 5 }) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-px overflow-hidden rounded-md border border-line-soft bg-line-soft shadow-od-1 sm:grid-cols-2',
        cols === 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4',
      )}
    >
      {children}
    </div>
  );
}

export type Status = 'ok' | 'watch' | 'off';
const STATUS: Record<Status, Tone> = { ok: 'success', watch: 'warn', off: 'danger' };

/** Statut à trois niveaux : pastille ronde + libellé. */
export function StatusChip({ status, children }: { status: Status; children: ReactNode }) {
  const tone = STATUS[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border px-2.5 py-0.5 text-[12px] font-semibold',
        TONES[tone],
      )}
    >
      <span aria-hidden className="h-2 w-2 rounded-pill bg-current" />
      {children}
    </span>
  );
}

/** Étiquette de source et de fraîcheur : FIN (clôtures comptables), OPE (ERP) ou SYNC (factures clients répliquées). */
export function SourceTag({ source, date }: { source: 'FIN' | 'OPE' | 'SYNC'; date?: string | null }) {
  const title = {
    FIN: 'Clôtures comptables (CEX)',
    OPE: 'Données opérationnelles (ERP Flex)',
    SYNC: 'Factures et avoirs clients, base SYNC (ERP Flex) : CA = somme des TotalHT',
  }[source];
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-sm border px-1.5 py-px font-body text-[11px] font-semibold normal-case tracking-normal',
        source === 'OPE' ? 'border-line bg-surface-2 text-ink-2' : 'border-navy-100 bg-navy-50 text-navy-600',
      )}
    >
      {source}
      {date && <span className="font-normal opacity-80">· {date}</span>}
    </span>
  );
}

/** Jauge horizontale avec repère optionnel (rythme attendu). */
export function Meter({
  value,
  marker,
  tone = 'navy',
  label,
}: {
  value: number;
  marker?: number;
  tone?: 'navy' | 'success' | 'warn' | 'danger';
  label: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  const fill = {
    navy: 'bg-navy-600',
    success: 'bg-success-600',
    warn: 'bg-warn-600',
    danger: 'bg-danger-600',
  }[tone];
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="relative h-2 w-full rounded-pill bg-surface-2"
    >
      <div
        className={cn('h-full rounded-pill transition-[width] duration-slow ease-od', fill)}
        style={{ width: `${pct}%` }}
      />
      {marker !== undefined && (
        <span
          aria-hidden
          className="absolute -top-1 h-4 w-0.5 rounded-pill bg-ink"
          style={{ left: `calc(${Math.max(0, Math.min(100, marker))}% - 1px)` }}
        />
      )}
    </div>
  );
}

/** Contrôle segmenté (choix exclusif). */
export function SegmentedControl<K extends string>({
  options,
  value,
  onChange,
  label,
  size = 'md',
}: {
  options: { key: K; label: ReactNode; count?: number }[];
  value: K;
  onChange: (k: K) => void;
  label: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex max-w-full overflow-x-auto overflow-y-hidden rounded-sm border border-line bg-surface p-0.5"
    >
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.key)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xs font-semibold transition-colors duration-fast ease-od',
              size === 'sm' ? 'px-2.5 py-1 text-[12.5px]' : 'px-3 py-1.5 text-[13px]',
              on ? 'bg-action text-on-action' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
            )}
          >
            {o.label}
            {o.count !== undefined && (
              <span className={cn('tnum text-[12px]', on ? 'opacity-85' : 'text-ink-3')}>{o.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Puce de filtre bascule. */
export function FilterChip({
  active,
  onClick,
  children,
  count,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  count?: number;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border px-3 py-1 text-[13px] font-semibold transition-colors duration-fast ease-od',
        active
          ? 'border-action bg-action text-on-action'
          : 'border-line bg-surface text-ink-2 hover:border-gray-400 hover:text-ink',
        className,
      )}
    >
      {children}
      {count !== undefined && (
        <span className={cn('tnum text-[12px]', active ? 'opacity-85' : 'text-ink-3')}>{count}</span>
      )}
    </button>
  );
}

export type AlertSeverity = 'bad' | 'warn' | 'good';

/** Liste de signaux : pastille ronde + texte, du plus grave au moins grave. */
export function AlertList({ items }: { items: { severity: AlertSeverity; text: ReactNode }[] }) {
  const tone: Record<AlertSeverity, string> = {
    bad: 'bg-danger-600',
    warn: 'bg-warn-600',
    good: 'bg-success-600',
  };
  const label: Record<AlertSeverity, string> = { bad: 'Alerte', warn: 'À surveiller', good: 'Aucun signal' };
  return (
    <ul className="divide-y divide-line-soft overflow-hidden rounded-md border border-line-soft bg-surface shadow-od-1">
      {items.map((a, i) => (
        <li key={i} className="flex items-start gap-3 px-4 py-3">
          <span aria-hidden className={cn('mt-[7px] h-2.5 w-2.5 shrink-0 rounded-pill', tone[a.severity])} />
          <div className="min-w-0 text-[14px] text-ink">
            <span className="sr-only">{label[a.severity]} : </span>
            {a.text}
          </div>
          <span className="ml-auto hidden shrink-0 text-[12px] font-semibold text-ink-3 sm:block">
            {label[a.severity]}
          </span>
        </li>
      ))}
    </ul>
  );
}
